import { money } from '../utils/money'

export const useFetch = async function (endpoint, type = 'POST', data) {
	try {
		const res = await fetch(endpoint, {
			method: type,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		}).then(r => r.json())
		return res
	} catch (error) {
		throw error
	}
}

export default {
	shopify: null,
	adding: false,
	success: false,
	loading: false,

	init() {
		this.cartEl = document.querySelector('[data-cart]')

		this.handleKeyUp = this.handleKeyUp.bind(this)

		window.addEventListener('keyup', this.handleKeyUp)

		this.load()
	},

	handleKeyUp(e) {
		if (e.key === 'Escape') {
			// Remove isOpen, open, close, toggle, handleCartOutsideClick, setupCartListeners, removeCartListeners methods and all references to them
		}
	},

	setShopify(cart) {
		this.shopify = cart
	},

	handleError(error) {
		return {
			success: false,
			error: error.message ?? 'Something went wrong.',
		}
	},

	handleSuccess() {
		return {
			success: true,
			cart: this.shopify,
		}
	},

	async load() {
		this.loading = true
		try {
			const cart = await useFetch('/cart.js', 'get')
			this.setShopify(cart)
		} catch (error) {
			return this.handleError(error)
		} finally {
			this.loading = false
			return this.handleSuccess()
		}
	},

	async updateLines(lineItems = {}) {
		if (this.loading) return
		this.loading = true

		try {
			const cart = await useFetch('/cart/update.js', 'post', {
				updates: lineItems,
			})
			this.setShopify(cart)
		} catch (error) {
			return this.handleError(error)
		} finally {
			this.loading = false
			return this.handleSuccess()
		}
	},

	async addLines(lineItems = [], onClose = () => {}) {
		this.loading = true
		this.adding = true

		try {
			const payload = await useFetch('/cart/add.js', 'post', {
				items: lineItems,
			})

			this.adding = false
			this.success = true

			setTimeout(() => {
				this.success = false
			}, 2000)

			onClose?.()

			return payload
		} catch (error) {
			return error
		} finally {
			this.load()
			return { status: 200, message: 'Added to cart' }
		}
	},

	async remove(item) {
		const newItems = {
			[item.id]: 0,
		}
		await this.updateLines(newItems)
	},

	async decrease(item) {
		const updatedQuantity = item.quantity - 1
		const newItems = {
			[item.id]: updatedQuantity,
		}
		await this.updateLines(newItems)
	},

	async increase(item) {
		const updatedQuantity = item.quantity + 1
		const newItems = {
			[item.id]: updatedQuantity,
		}
		await this.updateLines(newItems)
	},

	async goToCheckout() {
		try {
			// In development, log for debugging
			if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
				console.log('Development mode: checkout redirect may not work properly')
				console.log('In production, this will redirect to /checkout')
			}

			// Redirect to checkout - works in production
			window.location.href = '/checkout'
		} catch (error) {
			console.error('Error going to checkout:', error)
			// Fallback on error
			window.location.href = '/checkout'
		}
	},

	formatPrice(price, withTrailingZeros = false) {
		return money(price, withTrailingZeros, this.currency)
	},

	getSizeLabel(size) {
		const map = {
			s: 'Small',
			m: 'Medium',
			l: 'Large',
		}

		return map[size?.toLowerCase()] ?? size
	},

	get currency() {
		return this.shopify?.currency
	},

	get count() {
		return this.shopify?.item_count ?? 0
	},

	get hasItems() {
		return this.shopify?.item_count > 0 ?? false
	},

	get totalPrice() {
		return money(this.shopify?.total_price || 0, false, this.currency)
	},

	get total() {
		return money(this.shopify?.total_price || 0, false, this.currency)
	},

	get cartLines() {
		return this.shopify?.items || []
	},

	get isLoading() {
		return this.loading
	},

	get isAdding() {
		return this.adding
	},
}
